import React, { useEffect, useRef, useState } from "react";
import "./LiveBackdrop.css";
import retroGrid from "./grid.mp4";

const CITY_FEEDS = [
  {
    src: retroGrid,
    position: "50% 50%",
    label: "ANALOG CARRIER / 84",
  },
];

const SIGNAL_CLIPS = [
  {
    src: "/signals/earth-intercept.mp4?v=retro2",
    canvasFilter: "saturate(0.78) sepia(0.06) contrast(1.22) brightness(0.88)",
    label: "EARTH / LOW ORBIT",
    credit: "NASA JOHNSON / PUBLIC DOMAIN",
  },
  {
    src: "/signals/apollo-intercept.mp4",
    canvasFilter: "saturate(0.72) contrast(1.16) brightness(0.88)",
    label: "APOLLO 11 / JUL 1969",
    credit: "NASA / PUBLIC DOMAIN",
  },
  {
    src: "/signals/zadar-intercept.mp4?v=retro2",
    canvasFilter: "saturate(0.72) sepia(0.08) contrast(1.16) brightness(0.84)",
    label: "CROATIAN COAST / ADRIATIC",
    credit: "DELUCA G / PEXELS",
  },
];

const SIGNAL_INTERVAL = 7000;
const SIGNAL_DURATION = 1180;

function randomBetween(min, max) {
  if (window.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return min + (values[0] / 4294967295) * (max - min);
  }
  return min + Math.random() * (max - min);
}

function createRain(count = 250) {
  return Array.from({ length: count }, () => ({
    x: Math.random(),
    y: Math.random(),
    length: randomBetween(0.012, 0.048),
    speed: randomBetween(0.1, 0.32),
    depth: randomBetween(0.16, 1),
  }));
}

function coverMediaGeometry(mediaWidth, mediaHeight, width, height, scale = 1) {
  const ratio = Math.max(width / mediaWidth, height / mediaHeight) * scale;
  const drawWidth = mediaWidth * ratio;
  const drawHeight = mediaHeight * ratio;
  return {
    width: drawWidth,
    height: drawHeight,
    x: (width - drawWidth) / 2,
    y: (height - drawHeight) / 2,
  };
}

function signalFrame(progress) {
  if (progress < 0.06) return { opacity: 0, x: 20, y: -5, scale: 1.045 };
  if (progress < 0.12) return { opacity: 0.34, x: -13, y: 4, scale: 1.035 };
  if (progress < 0.19) return { opacity: 0.94, x: 6, y: 0, scale: 1.02 };
  if (progress < 0.26) return { opacity: 0.5, x: -5, y: 2, scale: 1.016 };
  if (progress < 0.76) return { opacity: 0.88, x: 0, y: 0, scale: 1.012 };
  if (progress < 0.84) return { opacity: 0.98, x: 9, y: -2, scale: 1.025 };
  if (progress < 0.9) return { opacity: 0.32, x: -16, y: 3, scale: 1.04 };
  if (progress < 0.95) return { opacity: 0.78, x: 4, y: 0, scale: 1.02 };
  return { opacity: 0, x: 0, y: 0, scale: 1 };
}

function drawSignalTears(context, video, geometry, height, time, opacity) {
  const sourceScaleY = video.videoHeight / geometry.height;
  const tick = Math.floor(time / 46);

  for (let index = 0; index < 7; index += 1) {
    const seed = Math.sin((tick + 1) * (index + 3) * 12.9898) * 43758.5453;
    const unit = seed - Math.floor(seed);
    const sliceHeight = 5 + unit * 32;
    const y = ((unit * 0.73 + index * 0.147) % 1) * height;
    const sourceY = Math.max(0, (y - geometry.y) * sourceScaleY);
    const sourceHeight = Math.min(video.videoHeight - sourceY, sliceHeight * sourceScaleY);
    if (sourceHeight <= 0) continue;

    const offset = (index % 2 === 0 ? 1 : -1) * (9 + unit * 32);
    context.globalAlpha = opacity * (0.28 + unit * 0.45);
    context.drawImage(
      video,
      0,
      sourceY,
      video.videoWidth,
      sourceHeight,
      geometry.x + offset,
      y,
      geometry.width,
      sliceHeight
    );
  }

  context.globalAlpha = 1;
}

export default function LiveBackdrop() {
  const canvasRef = useRef(null);
  const rainRef = useRef(createRain());
  const cityVideosRef = useRef([]);
  const signalVideosRef = useRef([]);
  const previousSignalRef = useRef(SIGNAL_CLIPS.length - 1);
  const activeSignalRef = useRef(null);
  const signalStartedAtRef = useRef(0);
  const [motionEnabled, setMotionEnabled] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [activeSignal, setActiveSignal] = useState(null);
  const [activeCity, setActiveCity] = useState(0);
  const [sceneShifting, setSceneShifting] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => {
      setReducedMotion(query.matches);
      if (query.matches) setMotionEnabled(false);
    };
    syncPreference();
    query.addEventListener?.("change", syncPreference);
    return () => query.removeEventListener?.("change", syncPreference);
  }, []);

  useEffect(() => {
    const enabled = motionEnabled && !reducedMotion;
    document.documentElement.dataset.siteMotion = enabled ? "on" : "off";
    window.dispatchEvent(new CustomEvent("site-motion-change", { detail: { enabled } }));
  }, [motionEnabled, reducedMotion]);

  useEffect(() => {
    const live = motionEnabled && !reducedMotion;
    const cityVideos = cityVideosRef.current;
    const signalVideos = signalVideosRef.current;

    if (!live) {
      cityVideos.forEach((video) => video?.pause());
      signalVideos.forEach((video) => video?.pause());
      setActiveSignal(null);
      setSceneShifting(false);
      activeSignalRef.current = null;
      return undefined;
    }

    cityVideos.forEach((video) => video?.play().catch(() => undefined));

    let stopped = false;
    let signalTimer = 0;
    let clearTimer = 0;
    let shiftTimer = 0;
    let shiftReleaseTimer = 0;

    const triggerSignal = () => {
      if (stopped || document.hidden) return;

      const nextSignal = (previousSignalRef.current + 1) % SIGNAL_CLIPS.length;
      previousSignalRef.current = nextSignal;
      const video = signalVideos[nextSignal];
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => undefined);
      }

      activeSignalRef.current = nextSignal;
      signalStartedAtRef.current = window.performance.now();
      setActiveSignal(nextSignal);

      shiftTimer = window.setTimeout(() => {
        setSceneShifting(true);
        setActiveCity((current) => (current + 1) % CITY_FEEDS.length);
        shiftReleaseTimer = window.setTimeout(() => setSceneShifting(false), 1450);
      }, 360);

      clearTimer = window.setTimeout(() => {
        video?.pause();
        activeSignalRef.current = null;
        signalStartedAtRef.current = 0;
        setActiveSignal(null);
      }, SIGNAL_DURATION);
    };

    signalTimer = window.setInterval(triggerSignal, SIGNAL_INTERVAL);

    const onVisibilityChange = () => {
      if (document.hidden) {
        cityVideos.forEach((video) => video?.pause());
      } else {
        cityVideos.forEach((video) => video?.play().catch(() => undefined));
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stopped = true;
      window.clearInterval(signalTimer);
      window.clearTimeout(clearTimer);
      window.clearTimeout(shiftTimer);
      window.clearTimeout(shiftReleaseTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      activeSignalRef.current = null;
      signalStartedAtRef.current = 0;
      cityVideos.forEach((video) => video?.pause());
      signalVideos.forEach((video) => video?.pause());
    };
  }, [motionEnabled, reducedMotion]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return undefined;

    let animationFrame = 0;
    let lastTime = 0;
    let stopped = false;
    let width = window.innerWidth;
    let height = window.innerHeight;
    let density = Math.min(window.devicePixelRatio || 1, 1.5);

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      density = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * density);
      canvas.height = Math.round(height * density);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    };

    const render = (time = 0) => {
      const live = motionEnabled && !reducedMotion;
      if (live && lastTime && time - lastTime < 30) {
        if (!stopped) animationFrame = window.requestAnimationFrame(render);
        return;
      }

      const delta = lastTime ? Math.min(42, time - lastTime) : 16;
      lastTime = time;
      context.setTransform(density, 0, 0, density, 0, 0);
      context.clearRect(0, 0, width, height);

      if (live) {
        const signalIndex = activeSignalRef.current;
        const signalVideo = signalIndex === null ? null : signalVideosRef.current[signalIndex];
        if (signalVideo?.videoWidth && signalVideo?.videoHeight) {
          const progress = Math.min(
            1,
            Math.max(0, (time - signalStartedAtRef.current) / SIGNAL_DURATION)
          );
          const frame = signalFrame(progress);
          if (frame.opacity > 0) {
            const geometry = coverMediaGeometry(
              signalVideo.videoWidth,
              signalVideo.videoHeight,
              width,
              height,
              frame.scale
            );

            context.save();
            context.globalAlpha = frame.opacity;
            context.filter = SIGNAL_CLIPS[signalIndex].canvasFilter;
            context.drawImage(
              signalVideo,
              geometry.x + frame.x,
              geometry.y + frame.y,
              geometry.width,
              geometry.height
            );

            context.filter = `${SIGNAL_CLIPS[signalIndex].canvasFilter} hue-rotate(155deg) saturate(2.4)`;
            context.globalCompositeOperation = "screen";
            context.globalAlpha = frame.opacity * 0.15;
            context.drawImage(
              signalVideo,
              geometry.x + frame.x + 7,
              geometry.y + frame.y,
              geometry.width,
              geometry.height
            );

            context.filter = SIGNAL_CLIPS[signalIndex].canvasFilter;
            context.globalCompositeOperation = "source-over";
            drawSignalTears(context, signalVideo, geometry, height, time, frame.opacity);
            context.restore();
          }
        }

        context.lineCap = "round";
        rainRef.current.forEach((drop) => {
          drop.y += drop.speed * (delta / 1000);
          drop.x -= drop.speed * 0.05 * (delta / 1000);
          if (drop.y > 1.08 || drop.x < -0.04) {
            drop.y = randomBetween(-0.18, -0.02);
            drop.x = randomBetween(0.05, 1.04);
          }
          const x = drop.x * width;
          const y = drop.y * height;
          context.beginPath();
          context.moveTo(x, y);
          context.lineTo(x - drop.length * height * 0.2, y + drop.length * height);
          context.strokeStyle = `rgba(184, 232, 245, ${0.06 + drop.depth * 0.14})`;
          context.lineWidth = 0.45 + drop.depth * 0.78;
          context.stroke();
        });
      }

      if (live && !stopped) animationFrame = window.requestAnimationFrame(render);
    };

    const start = () => {
      resize();
      window.cancelAnimationFrame(animationFrame);
      lastTime = 0;
      animationFrame = window.requestAnimationFrame(render);
    };

    start();
    window.addEventListener("resize", start);
    return () => {
      stopped = true;
      window.removeEventListener("resize", start);
      window.cancelAnimationFrame(animationFrame);
    };
  }, [motionEnabled, reducedMotion]);

  const live = motionEnabled && !reducedMotion;

  return (
    <>
      <div
        className={`liveBackdrop${sceneShifting ? " is-scene-shifting" : ""}`}
        aria-hidden="true"
      >
        <img
          className="cityFallback"
          src="/images/retro-grid-poster.jpg"
          alt=""
        />
        <div className="cityFeeds">
          {CITY_FEEDS.map((feed, index) => (
            <video
              ref={(element) => { cityVideosRef.current[index] = element; }}
              className={`cityFeed${activeCity === index ? " is-active" : ""}`}
              style={{ objectPosition: feed.position }}
              src={feed.src}
              muted
              loop
              playsInline
              preload={index === 0 ? "auto" : "metadata"}
              poster="/images/retro-grid-poster.jpg"
              key={feed.src}
            />
          ))}
        </div>

        <canvas ref={canvasRef} className="liveBackdrop__canvas" />

        <div className="signalSources">
          {SIGNAL_CLIPS.map((signal, index) => (
            <video
              ref={(element) => { signalVideosRef.current[index] = element; }}
              src={signal.src}
              muted
              playsInline
              preload="auto"
              key={signal.src}
            />
          ))}
        </div>

        <div className="liveBackdrop__atmosphere" />
        <div className="liveBackdrop__shade" />

        <div className="cityFeedStamp">
          <span className="cityFeedStamp__live">{live ? "LIVE" : "STILL"}</span>
          <span>{CITY_FEEDS[activeCity].label}</span>
        </div>

        {activeSignal !== null && (
          <div className="signalHijack__caption">
            <span>{SIGNAL_CLIPS[activeSignal].label}</span>
            <small>{SIGNAL_CLIPS[activeSignal].credit}</small>
          </div>
        )}
      </div>

      <button
        type="button"
        className="motionToggle"
        aria-pressed={motionEnabled}
        onClick={() => setMotionEnabled((current) => !current)}
      >
        Motion: {motionEnabled ? "on" : "off"}
      </button>
    </>
  );
}
